import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, CheckCircle2, X, FileText, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const initial = {
  username: "", password: "", password_confirm: "",
  name: "", email: "", phone: "", pesel: "", id_number: "", birth_date: "",
  address_street: "", address_number: "", address_apartment: "",
  address_zip: "", address_city: "",
  id_card_front_base64: "", id_card_back_base64: "",
  license_front_base64: "", license_back_base64: "",
  terms_accepted: false, data_consent: false,
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve((r.result || "").toString().split(",", 2)[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Register() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);  // null | 'pending' | 'active'
  const [showTerms, setShowTerms] = useState(false);
  const navigate = useNavigate();

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const uploadPhoto = (k) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Zdjęcie za duże (max 4 MB)");
      return;
    }
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, [k]: b64 }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirm) {
      toast.error("Hasła nie są identyczne");
      return;
    }
    setBusy(true);
    try {
      const { password_confirm, ...payload } = form;
      const res = await api.post("/auth/register", payload);
      setDone(res.data.status || "pending");
      toast.success(res.data.message || "Rejestracja przyjęta");
      if (res.data.status === "active") {
        setTimeout(() => navigate("/", { replace: true }), 1200);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rejestracja nie powiodła się");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <div className="max-w-md w-full border border-zinc-900 bg-white p-10 shadow-[8px_8px_0_0_#18181b] text-center" data-testid="register-success">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h1 className="font-heading font-black text-2xl uppercase tracking-tight mb-2">
            {done === "active" ? "Konto utworzone!" : "Rejestracja przyjęta"}
          </h1>
          <p className="text-sm text-zinc-600 mb-6">
            {done === "active"
              ? "Zaraz przekierujemy Cię do panelu."
              : "Administrator otrzyma powiadomienie o nowej rejestracji. Po zatwierdzeniu otrzymasz informację i będziesz mógł się zalogować."}
          </p>
          <Link to="/welcome" className="text-xs font-mono uppercase tracking-wider text-brand hover:underline">
            powrót do strony głównej →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-8">
      <form onSubmit={submit} className="max-w-2xl mx-auto bg-white border border-zinc-900 p-8 shadow-[8px_8px_0_0_#18181b] space-y-6" data-testid="register-form">
        <Link to="/welcome" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hover:text-brand">
          <ArrowLeft className="w-3 h-3" /> Powrót
        </Link>
        <div>
          <div className="font-label mb-1">// Rejestracja</div>
          <h1 className="font-heading font-black text-3xl uppercase tracking-tight">
            Utwórz konto
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            Po wysłaniu formularza administrator zatwierdza dostęp.
          </p>
        </div>

        <Section title="Dane logowania">
          <Grid>
            <Input label="Login" value={form.username} onChange={update("username")} required testid="reg-username" placeholder="a-z, 0-9, _ . - (min 4)" />
            <Input label="Hasło" type="password" value={form.password} onChange={update("password")} required testid="reg-password" placeholder="min 8 znaków" />
            <Input label="Powtórz hasło" type="password" value={form.password_confirm} onChange={update("password_confirm")} required testid="reg-password2" />
          </Grid>
        </Section>

        <Section title="Dane osobowe">
          <Grid>
            <Input label="Imię i nazwisko" value={form.name} onChange={update("name")} required testid="reg-name" />
            <Input label="Email (na powiadomienia)" value={form.email} onChange={update("email")} type="email" testid="reg-email" />
            <Input label="Numer telefonu" value={form.phone} onChange={update("phone")} type="tel" testid="reg-phone" />
            <Input label="Data urodzenia" value={form.birth_date} onChange={update("birth_date")} type="date" testid="reg-birth" />
            <Input label="PESEL" value={form.pesel} onChange={update("pesel")} maxLength={11} pattern="[0-9]{11}" testid="reg-pesel" />
            <Input label="Numer dowodu osobistego" value={form.id_number} onChange={update("id_number")} testid="reg-idnum" />
          </Grid>
        </Section>

        <Section title="Adres zamieszkania">
          <Grid>
            <Input label="Ulica" value={form.address_street} onChange={update("address_street")} testid="reg-street" />
            <Input label="Nr domu" value={form.address_number} onChange={update("address_number")} testid="reg-num" />
            <Input label="Nr mieszkania (opc.)" value={form.address_apartment} onChange={update("address_apartment")} testid="reg-apt" />
            <Input label="Kod pocztowy" value={form.address_zip} onChange={update("address_zip")} testid="reg-zip" placeholder="00-000" />
            <Input label="Miasto" value={form.address_city} onChange={update("address_city")} testid="reg-city" />
          </Grid>
        </Section>

        <Section title="Dokumenty (zdjęcia — kamera lub galeria)">
          <Grid>
            <PhotoUpload label="Dowód — przód" value={form.id_card_front_base64} onChange={uploadPhoto("id_card_front_base64")} testid="reg-photo-id-front" />
            <PhotoUpload label="Dowód — tył" value={form.id_card_back_base64} onChange={uploadPhoto("id_card_back_base64")} testid="reg-photo-id-back" />
            <PhotoUpload label="Prawo jazdy — przód" value={form.license_front_base64} onChange={uploadPhoto("license_front_base64")} testid="reg-photo-lic-front" />
            <PhotoUpload label="Prawo jazdy — tył" value={form.license_back_base64} onChange={uploadPhoto("license_back_base64")} testid="reg-photo-lic-back" />
          </Grid>
          <p className="text-[10px] text-zinc-500 font-mono mt-2">
            // Na telefonie kliknięcie przycisku otworzy aparat. Na desktopie — galerię.
          </p>
        </Section>

        <Section title="Zgody">
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={form.terms_accepted} onChange={update("terms_accepted")} required data-testid="reg-terms" className="mt-1" />
            <span>
              Akceptuję{" "}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                data-testid="reg-terms-open"
                className="text-brand font-semibold underline underline-offset-2 hover:no-underline inline-flex items-center gap-1"
              >
                regulamin
                <ExternalLink className="w-3 h-3" />
              </button>{" "}
              korzystania z platformy GoPossible.
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm cursor-pointer mt-3">
            <input type="checkbox" checked={form.data_consent} onChange={update("data_consent")} required data-testid="reg-consent" className="mt-1" />
            <span>
              Wyrażam zgodę na przetwarzanie moich danych osobowych przez GoPossible Sp. z o.o.
              w celach związanych z realizacją usług kurierskich (RODO).
            </span>
          </label>
        </Section>

        <button
          type="submit"
          disabled={busy || !form.terms_accepted || !form.data_consent}
          data-testid="btn-register-submit"
          className="w-full bg-zinc-900 hover:bg-brand text-white py-4 font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          {busy ? "Wysyłam..." : "Zarejestruj się"}
        </button>
      </form>

      {showTerms && (
        <TermsModal
          onClose={() => setShowTerms(false)}
          onAccept={() => { setForm({ ...form, terms_accepted: true }); setShowTerms(false); }}
        />
      )}
    </div>
  );
}

function TermsModal({ onClose, onAccept }) {
  return (
    <div
      className="fixed inset-0 z-[1300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="terms-overlay"
    >
      <div
        className="bg-white w-full max-w-4xl h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="terms-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-brand" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                // Regulamin
              </div>
              <h2 className="font-heading font-black text-xl uppercase tracking-tight">
                Regulamin platformy GoPossible
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/regulamin.html"
              target="_blank"
              rel="noreferrer"
              data-testid="terms-open-tab"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-brand"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Otwórz w nowej karcie
            </a>
            <button
              onClick={onClose}
              data-testid="terms-close"
              className="w-10 h-10 rounded-full bg-zinc-100 hover:bg-zinc-900 hover:text-white flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <iframe
          src="/regulamin.html"
          title="Regulamin platformy GoPossible"
          data-testid="terms-iframe"
          className="flex-1 w-full border-0"
        />
        <div className="px-6 py-4 border-t border-zinc-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="text-xs text-zinc-500">
            Zapoznaj się z regulaminem przed akceptacją. Możesz też pobrać go w PDF.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              type="button"
              data-testid="terms-cancel"
              className="px-4 py-2 border-2 border-zinc-900 text-xs font-bold uppercase tracking-wider rounded-lg hover:bg-zinc-900 hover:text-white"
            >
              Anuluj
            </button>
            <button
              onClick={onAccept}
              type="button"
              data-testid="terms-accept"
              className="px-5 py-2 bg-brand hover:bg-brand-hover text-white text-xs font-bold uppercase tracking-wider rounded-lg inline-flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Akceptuję regulamin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-zinc-200 pt-5">
      <div className="font-label mb-3">{`// ${title}`}</div>
      {children}
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}
function Input({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">
        {label}{props.required && <span className="text-brand ml-1">*</span>}
      </span>
      <input
        data-testid={testid}
        {...props}
        className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900"
      />
    </label>
  );
}
function PhotoUpload({ label, value, onChange, testid }) {
  return (
    <label className="block cursor-pointer">
      <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</span>
      <div className={`border-2 ${value ? "border-emerald-500 bg-emerald-50" : "border-dashed border-zinc-300 bg-zinc-50"} px-3 py-3 flex items-center gap-2`}>
        <Camera className="w-4 h-4 text-zinc-500" />
        <span className="text-xs flex-1 truncate">
          {value ? "✓ Załadowane" : "Kliknij i wybierz / zrób zdjęcie"}
        </span>
      </div>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onChange}
        data-testid={testid}
        className="hidden"
      />
    </label>
  );
}
